// Need correct import for callable
import type { GradingScale } from '@dosfilos/domain';
import { callable } from '../callable';
/** M1·G1: una fila de avance guardado del acta. */
export interface ActaDraftRowDto {
  enrollmentId: string;
  grade?: number;
  label?: string;
  status: 'approved' | 'failed' | 'dropped';
  rev: number;
  savedByName: string | null;
  savedAt: string;
}

export interface ActaDraftSaveResult {
  saved: ActaDraftRowDto[];
  cleared: string[];
  conflicts: ActaDraftRowDto[];
}

export interface RecordGradeRequest {
  tenantId: string;
  enrollmentId: string;
  studentId: string;
  sectionId: string;
  value: number;
}

export interface CloseGradeRequest {
  tenantId: string;
  enrollmentId: string;
  finalStatus?: 'approved' | 'failed' | 'dropped';
}

export interface SectionGradebookRow {
  enrollmentId: string;
  studentId: string;
  studentName: string;
  /** Opcional: el servidor viejo no lo manda (Hosting despliega antes). */
  studentCode?: string | null;
  percentCurrent: number | null;
  gradeSuggested: number | null;
  /** Opcionales: el servidor viejo no los manda (Hosting despliega antes). */
  gradeSuggestedLabel?: string | null;
  suggestedPasses?: boolean | null;
  gradeRecorded: number | null;
  gradeRecordedLabel?: string | null;
  status: string;
  /** Deuda NUESTRA: entregó y nadie corrigió. */
  awaitingGrading: number;
  /** Deuda del ALUMNO: no entregó nada. Campos distintos a propósito. */
  missing: number;
  /** De `missing`, lo que todavía se puede entregar. Cero significa que no hay
   *  nada que reclamar: sólo queda cerrar. */
  missingOpen: number;
}

export interface SectionGradebook {
  sectionId: string;
  courseName: string;
  /** El titular, para la cabecera y el acta (PR 3A). `null` sin titular o
   *  ficha ilegible; ausente si responde un servidor anterior. */
  facultyName?: string | null;
  status: string;
  /** `scale`: la escala del seminario; texto si responde el servidor viejo
   *  (ver lib/grading.ts). `passingGrade`: la del curso, o null. */
  policy: {
    scale: GradingScale | '1-7' | '0-100';
    exigencia?: number;
    passingGrade: number | null;
  };
  weightTotal: number;
  publishedAssignments: number;
  unmarkedAssignments: number;
  rows: SectionGradebookRow[];
  /** Quién cerró el ACTA y cuándo (ISO). Null si está abierta, o si la cerró la
   *  importación. Opcionales: un servidor anterior no los manda. */
  closedAt?: string | null;
  closedByName?: string | null;
}

export interface PerformanceStudentRefDto {
  studentId: string;
  studentName: string;
  grade?: number;
}

export interface AssignmentPerformanceDto {
  assignmentId: string;
  name: string;
  dueAt?: string;
  weight: number;
  /** El promedio de acta: incluye los ceros de quien no entregó. */
  average: number | null;
  /** El promedio de QUIENES ENTREGARON. La distancia con el otro es lo que
   *  separa un problema de enseñanza de uno de entrega.
   *
   *  OPCIONAL A PROPÓSITO, aunque el servidor SIEMPRE lo mande. Acá Hosting
   *  despliega ANTES que Backend: durante esa ventana la pantalla nueva habla
   *  con el callable viejo, que no conoce este campo, y llega `undefined`. Si
   *  el tipo dijera `number | null`, el compilador dejaría pasar un
   *  `.toFixed()` directo y la pantalla se caería entera —pasó, en local, con
   *  el emulador sin reiniciar—. Declararlo opcional obliga a cada consumidor
   *  a decidir qué hacer cuando no está. */
  averageDelivered?: number | null;
  expected: number;
  delivered: number;
  late: number;
  graded: number;
  failed: number;
  /** Acotada en el servidor: un curso de quinientos no descarga quinientos. */
  missingStudents: PerformanceStudentRefDto[];
  /** Quienes ENTREGARON y no alcanzaron. No incluye los ceros por no entregar:
   *  ésos ya están en `missingStudents`. */
  failedStudents: PerformanceStudentRefDto[];
}

export interface SectionPerformance {
  sectionId: string;
  courseName: string;
  scale: GradingScale | '1-7' | '0-100';
  passingGrade: number;
  distribution: Array<{ from: number; to: number; count: number }>;
  assignments: AssignmentPerformanceDto[];
  onTime: number;
  lateTotal: number;
  neverDelivered: number;
}

export const gradeApi = {
  /** Cómo RINDIÓ el curso. Otra pregunta que el libro de notas: ése dice qué
   *  nota lleva cada uno, éste dice cómo fue el curso. */
  getSectionPerformance: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<SectionPerformance> => {
    const fn = callable<typeof data, SectionPerformance>('getSectionPerformance');
    return (await fn(data)).data;
  },

  getSectionGradebook: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<SectionGradebook> => {
    const fn = callable<typeof data, SectionGradebook>('getSectionGradebook');
    return (await fn(data)).data;
  },

  reopenSectionGradebook: async (data: {
    tenantId: string;
    sectionId: string;
    reason: string;
  }): Promise<{ sectionId: string }> => {
    const fn = callable<typeof data, { sectionId: string }>('reopenSectionGradebook');
    return (await fn(data)).data;
  },

  /** M1·G1: el avance guardado del acta (solo quien puede cerrarla). */
  getActaDraft: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<{ rows: ActaDraftRowDto[] }> => {
    const fn = callable<typeof data, { rows: ActaDraftRowDto[] }>('getActaDraft');
    return (await fn(data)).data;
  },

  /** M1·G1: guarda avance. Una fila que cambió otra persona desde que se abrió
   *  NO se pisa: vuelve en `conflicts`, con quién y cuándo. */
  saveActaDraft: async (data: {
    tenantId: string;
    sectionId: string;
    rows: {
      enrollmentId: string;
      grade?: number;
      label?: string;
      status: 'approved' | 'failed' | 'dropped';
      baseRev: number | null;
    }[];
  }): Promise<ActaDraftSaveResult> => {
    const fn = callable<typeof data, ActaDraftSaveResult>('saveActaDraft');
    return (await fn(data)).data;
  },

  closeSectionGradebook: async (data: {
    tenantId: string;
    sectionId: string;
    /** En escala numérica `grade`; en letras `label`. El estado lo decide el
     *  servidor con la escala; del cliente solo vale «retirado». */
    rows: {
      enrollmentId: string;
      grade?: number;
      label?: string;
      status: 'approved' | 'failed' | 'dropped';
    }[];
    acknowledgeWeights?: boolean;
  }): Promise<{ closed: number; weightTotal: number }> => {
    const fn = callable<typeof data, { closed: number; weightTotal: number }>(
      'closeSectionGradebook',
    );
    return (await fn(data)).data;
  },

  recordGrade: async (data: RecordGradeRequest): Promise<{ id: string }> => {
    const recordGradeFn = callable<RecordGradeRequest, { id: string }>('recordGrade');
    const result = await recordGradeFn(data);
    return result.data;
  },

  closeGrade: async (data: CloseGradeRequest): Promise<{ id: string }> => {
    const closeGradeFn = callable<CloseGradeRequest, { id: string }>('closeGrade');
    const result = await closeGradeFn(data);
    return result.data;
  },
};
